import React, { useCallback, useEffect, useRef, useState } from 'react'
// NOTE: `react-leaflet` (v1.9.x) + `leaflet` are the map provider already used
// by this app (see order-details/update-address-btn). Their types resolve loosely
// because `allowJs` is on and the `leaflet` package ships no `types` field, so
// TypeScript infers from its JS rather than @types/leaflet. The import is kept
// inside this component so the provider stays swappable.
import { Map as LeafletMap } from 'react-leaflet'
import VectorBasemap from '../map/vector-basemap'
import { ReactComponent as HouseIcon } from '../../assets/icons/house-active.svg'
import { useEnvVars } from '../../hooks/useEnvVars'
import { Coordinates } from '../../types/Order'
import {
  MapWrapper,
  PinHead,
  PinStem,
  PinTarget,
  PinWrapper,
} from './pin-map-styled'


/**
 * A request for the map to move itself somewhere, carrying its own zoom so that
 * asking for a move never means changing the `zoom` prop — react-leaflet reacts
 * to that prop on its own with a `setZoom` jump, which would race the fly.
 */
export type MapTarget = {
  center: Coordinates
  zoom: number
}

/**
 * Who moved the map, for a reported centre.
 *
 * `'drag'` means the customer dragged it — Leaflet's `dragend` is raised by its
 * drag handler alone, so this is the one report that proves the customer took
 * control of the pin.
 *
 * `'settle'` means the map came to rest for any other reason: the end of a
 * requested fly, or a move Leaflet made on its own. The notable case is a window
 * resize — a mobile soft keyboard opening over the page — which Leaflet answers
 * with `invalidateSize({ debounceMoveend: true })`, panning the pane and firing
 * `moveend` 200ms later. That is a real settle worth reporting, but it is NOT
 * the customer positioning their door, and callers must not read it as such.
 */
export type MoveSource = 'drag' | 'settle'

type Props = {
  /**
   * Fallback opening centre / starting pin location — the best point known
   * without asking anyone (the link's `?lat`/`?lng`, else the city fallback).
   * Only used if no `target` has been requested by the time Leaflet is created;
   * never used to move the map afterwards.
   */
  center: Coordinates
  /** Fallback opening zoom, paired with `center`. */
  zoom: number
  /**
   * Where the map should move itself to — an address the customer picked, or a
   * better starting point that arrived asynchronously. `null` leaves the map
   * wherever the customer left it.
   *
   * A target requested BEFORE Leaflet exists becomes the view the map is
   * created on, so the customer never sees it open somewhere else and jerk
   * across. One requested after creation is flown to. Either way the view the
   * customer ends up looking at is the target, which is what makes the centre
   * they confirm the centre they actually saw.
   *
   * Each request must be a NEW object: the fly is triggered by identity, not by
   * comparing coordinates, so that re-picking the same suggestion after dragging
   * away still flies back to it. (Diffing by value cannot express "go there
   * again" — react-leaflet's own `center` handling has exactly that blind spot,
   * which is why the fly is done imperatively below.) The page satisfies this by
   * building a fresh target object per pick.
   *
   * MUST NOT be fed from `onChange`'s output. `target` is an instruction *into*
   * the map and `onChange` reports what the map settled on; wiring the second
   * back into the first makes every reported move request another move, which
   * is a pan feedback loop. They are separate pieces of state by design.
   */
  target?: MapTarget | null
  /**
   * Called with the map centre (the pin location) every time the map settles,
   * along with what moved it. Every settle is reported, so the centre a caller
   * holds always matches what the customer can see; `source` is how a caller
   * tells the customer's own drag apart from a move Leaflet made by itself.
   */
  onChange: (coords: Coordinates, source: MoveSource) => void
}

/**
 * A single-purpose, provider-isolated map with a centre-anchored pin. The pin
 * is a fixed overlay and the map pans under it, so the confirmed coordinate is
 * always the map centre. Swapping to another provider (MapLibre, 2GIS, Google)
 * only requires re-implementing this component against the same `Props`.
 */
export default function PinMap(props: Props) {
  const { onChange, target, zoom } = props
  const { env } = useEnvVars()
  const [dragging, setDragging] = useState(false)

  /**
   * The live Leaflet instance, held in STATE rather than read off a ref at the
   * moment we happen to want it.
   *
   * This is load-bearing, not a style choice. react-leaflet builds a brand new
   * `L.Map` in every `componentDidMount`, and React 18 `StrictMode` (which this
   * app renders under, see `src/index.tsx`) double-invokes that on a subtree's
   * first mount: mount, simulated unmount — which calls `remove()` on the map —
   * then mount again. Our effects run in the passive flush BETWEEN those two,
   * so a ref read there hands back the instance that is about to be thrown
   * away. Flying that one animates a detached map: the fly genuinely runs, and
   * the customer sees none of it, because the map they are looking at is the
   * replacement that was created on the opening view and never told to move.
   *
   * Keying off state instead means the swap re-renders and re-runs the fly
   * effect against whichever instance is currently real, so a target can never
   * be stranded on a discarded map.
   */
  const [leafletElement, setLeafletElement] = useState<any>(null)

  /**
   * The view Leaflet is created on, captured when the mount timer fires rather
   * than frozen at first render, plus the `target` (if any) that was already
   * pending at that moment.
   *
   * Capturing it late is what lets an asynchronous better-guess point that beat
   * the timer become the map's BIRTH view: the map opens on the customer's own
   * neighbourhood with no city-wide frame and no fly. `null` until the timer
   * fires, which is also the "not created yet" flag.
   */
  const [opening, setOpening] = useState<{
    view: MapTarget
    /** Identity of the target folded into `view`; that one must not re-fly. */
    bornOn?: MapTarget | null
  } | null>(null)

  // Latest props, so the mount timer reads what is true when it fires instead
  // of what was true when it was armed. Assigned during render (idempotent) so
  // it is never stale by the time the timeout runs.
  const latest = useRef({ center: props.center, zoom, target })
  latest.current = { center: props.center, zoom, target }

  // Create the map only after layout has settled so Leaflet sizes its tiles
  // correctly (same approach as the existing address map). The delay is a
  // layout concern only — nothing about correctness may depend on who wins the
  // race against it, which is the whole point of capturing the view here.
  useEffect(() => {
    const timer = setTimeout(() => {
      const pending = latest.current
      setOpening({
        view: pending.target ?? {
          center: pending.center,
          zoom: pending.zoom,
        },
        bornOn: pending.target,
      })
    }, 300)
    return () => clearTimeout(timer)
  }, [])

  /**
   * Hands us each `L.Map` as it becomes usable. Leaflet calls `whenReady` with
   * `{ target: map }`, and react-leaflet re-registers it on every
   * `componentDidMount` — so this fires again for the replacement instance
   * `StrictMode` creates, which is exactly the signal the fly effect needs.
   */
  const handleReady = useCallback((event: any) => {
    setLeafletElement(event?.target ?? null)
  }, [])

  // Fly to each requested target. Done imperatively rather than by handing
  // `target` to react-leaflet as `center`, because react-leaflet only moves when
  // the `center` value *differs* from the previous one (`shouldUpdateCenter`) —
  // so a request to return to somewhere the map has already been asked to go
  // (pick a suggestion, drag away, pick it again) would be silently dropped.
  //
  // `leafletElement` is a dependency so a target requested before Leaflet
  // exists — or while an instance was being swapped underneath us — is applied
  // as soon as there is a live map to apply it to.
  useEffect(() => {
    if (!leafletElement || !target) {
      return
    }

    // The map was created looking at this very target; it is already there and
    // flying would only wobble it. Compared by identity, not value, so that
    // re-picking the same place after dragging away (a fresh target object)
    // still flies back to it.
    if (opening && target === opening.bornOn) {
      return
    }

    // flyTo, not setView: the customer needs to see the map travel to the
    // address they picked, otherwise the view silently becomes somewhere else.
    leafletElement.flyTo([target.center.lat, target.center.lng], target.zoom)
  }, [leafletElement, target, opening])

  const reportCenter = (event: any, source: MoveSource) => {
    const center = event.target.getCenter()
    onChange({ lat: center.lat, lng: center.lng }, source)
  }

  if (!opening) {
    return <MapWrapper data-testid="pin-map" />
  }

  return (
    <MapWrapper data-testid="pin-map">
      <LeafletMap
        className="qu-map"
        whenReady={handleReady}
        // The opening view only, and fixed for the life of the map: it is held
        // in state, so a later `center`/`zoom` prop cannot reach Leaflet and
        // race the imperative fly above. Every later move is either the
        // customer's own drag or that fly.
        center={[opening.view.center.lat, opening.view.center.lng]}
        zoom={opening.view.zoom}
        maxZoom={19}
        minZoom={5}
        zoomControl={false}
        scrollWheelZoom
        // Belt and braces: neither `center` nor `zoom` changes after mount, but
        // should react-leaflet ever act on one, animate rather than teleport.
        useFlyTo
        // Fires for every settle, including ones Leaflet initiates itself (a
        // resize-driven `invalidateSize`), so it cannot stand in for "the
        // customer moved the pin" — hence the source argument.
        onMoveEnd={(e: any) => reportCenter(e, 'settle')}
        onDragStart={() => setDragging(true)}
        onDragEnd={(e: any) => {
          setDragging(false)
          reportCenter(e, 'drag')
        }}
      >
        <VectorBasemap styleUrl={env.TILES_STYLE_URL ?? ''} />
      </LeafletMap>
      <PinTarget />
      <PinWrapper dragging={dragging} data-testid="pin-marker">
        <PinStem />
        <PinHead>
          <HouseIcon />
        </PinHead>
      </PinWrapper>
    </MapWrapper>
  )
}
